import React from "react";

/* ─────────────────────────────────────────────────────────────
   SHIMMER PRIMITIVES
   Provides industry-standard skeleton loaders for every
   home-page section: Banner, ProductCard, DealCard, Brand,
   Blog, and a generic line/box helper.
───────────────────────────────────────────────────────────── */

/* ── CSS injected once ──────────────────────────────────────── */
const ShimmerStyles = () => (
  <style>{`
    @keyframes shimmer-sweep {
      0%   { background-position: -600px 0; }
      100% { background-position: 600px 0; }
    }
    .shimmer-bg {
      background: linear-gradient(
        90deg,
        #e8eaed 0px,
        #f4f5f7 40px,
        #e8eaed 80px
      );
      background-size: 600px 100%;
      animation: shimmer-sweep 1.4s ease-in-out infinite;
      border-radius: 6px;
    }
    /* Auto dark mode via .dark on html */
    .dark .shimmer-bg {
      background: linear-gradient(
        90deg,
        rgba(255,255,255,0.06) 0px,
        rgba(255,255,255,0.12) 40px,
        rgba(255,255,255,0.06) 80px
      );
      background-size: 600px 100%;
      animation: shimmer-sweep 1.4s ease-in-out infinite;
    }
    /* Dark variant for sections with dark background */
    .shimmer-bg-dark {
      background: linear-gradient(
        90deg,
        rgba(255,255,255,0.06) 0px,
        rgba(255,255,255,0.13) 40px,
        rgba(255,255,255,0.06) 80px
      );
      background-size: 600px 100%;
      animation: shimmer-sweep 1.4s ease-in-out infinite;
      border-radius: 6px;
    }
  `}</style>
);

let stylesInjected = false;

/* ── Generic shimmer block ──────────────────────────────────── */
export const ShimmerBox = ({
  w = "100%",
  h = 16,
  rounded = 6,
  dark = false,
  className = "",
  style = {},
}) => {
  if (!stylesInjected) stylesInjected = true;
  return (
    <div
      className={`${dark ? "shimmer-bg-dark" : "shimmer-bg"} ${className}`}
      style={{ width: w, height: h, borderRadius: rounded, ...style }}
    />
  );
};

/* ── Banner shimmer ─────────────────────────────────────────── */
export const BannerShimmer = () => (
  <>
    <ShimmerStyles />
    <div
      className="relative w-full overflow-hidden"
      style={{ aspectRatio: "16/5", background: "#e2e5e9" }}
    >
      <div className="shimmer-bg" style={{ width: "100%", height: "100%", borderRadius: 0 }} />
      {/* Fake dot indicators */}
      <div
        style={{
          position: "absolute",
          bottom: 12,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          gap: 8,
        }}
      >
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className="shimmer-bg"
            style={{
              width: i === 0 ? 20 : 8,
              height: 8,
              borderRadius: 99,
              opacity: 0.6,
            }}
          />
        ))}
      </div>
    </div>
  </>
);

/* ── Product Card shimmer (matches ProductCard layout) ───────── */
export const ProductCardShimmer = ({ dark = false }) => (
  <div
    style={{
      background: dark ? "rgba(255,255,255,0.05)" : "var(--pc-card-bg)",
      border: dark ? "1px solid rgba(255,255,255,0.08)" : "1px solid var(--pc-card-border)",
      borderRadius: 20,
      padding: 16,
      display: "flex",
      flexDirection: "column",
      gap: 12,
      width: "100%",
      minWidth: 260,
      boxSizing: "border-box",
    }}
  >
    {/* Image area */}
    <ShimmerBox h={200} rounded={14} dark={dark} />

    {/* Badge row */}
    <div style={{ display: "flex", gap: 8 }}>
      <ShimmerBox w={52} h={20} rounded={99} dark={dark} />
      <ShimmerBox w={72} h={20} rounded={99} dark={dark} />
    </div>

    {/* Title lines */}
    <ShimmerBox h={16} rounded={6} dark={dark} />
    <ShimmerBox w="70%" h={14} rounded={6} dark={dark} />

    {/* Spec chips */}
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {[90, 80, 70, 60, 80, 70].map((w, i) => (
        <ShimmerBox key={i} w={w} h={22} rounded={99} dark={dark} />
      ))}
    </div>

    {/* Price + button */}
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <ShimmerBox w={90} h={22} rounded={6} dark={dark} />
        <ShimmerBox w={60} h={14} rounded={6} dark={dark} />
      </div>
      <ShimmerBox w={100} h={36} rounded={10} dark={dark} />
    </div>
  </div>
);

/* ── Horizontal carousel of product card shimmers ───────────── */
export const ProductCarouselShimmer = ({ count = 5, dark = false }) => (
  <>
    <ShimmerStyles />
    <div style={{ display: "flex", overflowX: "hidden", gap: 20, paddingBottom: 16 }}>
      {[...Array(count)].map((_, i) => (
        <div key={i} style={{ minWidth: 280, maxWidth: 290, flex: "0 0 auto" }}>
          <ProductCardShimmer dark={dark} />
        </div>
      ))}
    </div>
  </>
);

/* ── Deal Card shimmer (dark bg section) ────────────────────── */
export const DealCardShimmer = () => (
  <>
    <ShimmerStyles />
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
        gap: 20,
      }}
    >
      {[...Array(4)].map((_, i) => (
        <div
          key={i}
          style={{
            borderRadius: 24,
            padding: 24,
            background: "rgba(255,255,255,0.06)",
            display: "flex",
            flexDirection: "column",
            gap: 14,
            minHeight: 260,
            border: "1px solid rgba(255,255,255,0.09)",
          }}
        >
          {/* Icon placeholder */}
          <ShimmerBox w={48} h={48} rounded={14} dark />
          {/* Title */}
          <ShimmerBox h={18} rounded={6} dark />
          <ShimmerBox w="60%" h={14} rounded={6} dark />
          {/* Price range chips */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
            {[80, 90, 75, 85].map((w, j) => (
              <ShimmerBox key={j} w={w} h={32} rounded={99} dark />
            ))}
          </div>
        </div>
      ))}
    </div>
  </>
);

/* ── Brand logo shimmer ──────────────────────────────────────── */
export const BrandShimmer = ({ count = 8, dark = false }) => (
  <>
    <ShimmerStyles />
    <div className="flex overflow-x-auto gap-4 pb-4 scrollbar-hide">
      {[...Array(count)].map((_, i) => (
        <div
          key={i}
          className="flex flex-col items-center justify-center min-w-[140px] max-w-[140px] h-[130px] rounded-xl bg-white dark:bg-[#1a1a24] border border-gray-100 dark:border-gray-800 p-4 shrink-0 shadow-sm"
        >
          <ShimmerBox w={64} h={40} rounded={8} dark={dark} className="mb-3" />
          <ShimmerBox w={80} h={14} rounded={6} dark={dark} />
        </div>
      ))}
    </div>
  </>
);

/* ── Blog card shimmer ───────────────────────────────────────── */
export const BlogShimmer = ({ count = 3, dark = false }) => (
  <>
    <ShimmerStyles />
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
      {[...Array(count)].map((_, i) => (
        <div key={i} className="flex flex-col gap-4">
          <ShimmerBox w="100%" className="aspect-video rounded-[2rem]" dark={dark} />
          <div className="flex gap-2 px-2">
            <ShimmerBox w={80} h={14} rounded={99} dark={dark} />
            <ShimmerBox w={60} h={14} rounded={99} dark={dark} />
          </div>
          <div className="flex flex-col gap-2 px-2">
            <ShimmerBox w="95%" h={20} rounded={6} dark={dark} />
            <ShimmerBox w="70%" h={20} rounded={6} dark={dark} />
            <ShimmerBox w="100%" h={14} rounded={6} dark={dark} className="mt-1" />
            <ShimmerBox w="60%" h={14} rounded={6} dark={dark} />
          </div>
        </div>
      ))}
    </div>
  </>
);

/* ── Hot Deal / Spotlight shimmer ───────────────────────────────────── */
export const HotDealShimmer = ({ dark = false }) => (
  <>
    <ShimmerStyles />
    <section className="py-8 md:py-10 bg-[#FDFBF7] dark:bg-gray-900 relative overflow-hidden">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="mb-8 md:mb-10 max-w-2xl space-y-3">
          <ShimmerBox w={180} h={14} rounded={99} dark={dark} />
          <ShimmerBox w={320} h={38} rounded={10} dark={dark} />
          <ShimmerBox w="90%" h={16} rounded={6} dark={dark} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-center">
          <div className="bg-[#F6F4EE] dark:bg-gray-800 rounded-[1.5rem] md:rounded-[2rem] p-6 md:p-8 flex items-center justify-center min-h-[300px] md:min-h-[400px] border border-gray-200/50 dark:border-gray-700/50">
            <ShimmerBox w="80%" h={220} rounded={16} dark={dark} />
          </div>

          <div className="flex flex-col justify-center space-y-5">
            <ShimmerBox w={100} h={16} rounded={6} dark={dark} />
            <ShimmerBox w="85%" h={32} rounded={8} dark={dark} />
            <div className="flex flex-wrap gap-2 pt-2">
              <ShimmerBox w={90} h={32} rounded={8} dark={dark} />
              <ShimmerBox w={80} h={32} rounded={8} dark={dark} />
              <ShimmerBox w={100} h={32} rounded={8} dark={dark} />
              <ShimmerBox w={75} h={32} rounded={8} dark={dark} />
            </div>
            <div className="flex items-center gap-3 pt-2">
              <ShimmerBox w={140} h={38} rounded={8} dark={dark} />
              <ShimmerBox w={80} h={24} rounded={6} dark={dark} />
            </div>
            <div className="flex flex-wrap gap-3 pt-4">
              <ShimmerBox w={160} h={48} rounded={99} dark={dark} />
              <ShimmerBox w={140} h={48} rounded={99} dark={dark} />
            </div>
          </div>
        </div>
      </div>
    </section>
  </>
);

/* ── Testimonial Shimmer ────────────────────────────────────────────── */
export const TestimonialShimmer = ({ count = 4, dark = false }) => (
  <>
    <ShimmerStyles />
    <section className="pt-8 pb-12 bg-white dark:bg-black overflow-hidden relative">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="text-center mb-8 flex flex-col items-center gap-3">
          <ShimmerBox w={320} h={32} rounded={8} dark={dark} />
          <ShimmerBox w={240} h={16} rounded={6} dark={dark} />
        </div>

        <div className="flex overflow-x-auto pb-6 pt-2 gap-5 scrollbar-hide">
          {[...Array(count)].map((_, i) => (
            <div
              key={i}
              className="w-[280px] sm:w-[295px] shrink-0 bg-white dark:bg-[#111] rounded-2xl border border-gray-100 dark:border-gray-800 p-6 sm:p-7 flex flex-col gap-4 shadow-sm"
            >
              <ShimmerBox w={42} h={42} rounded={12} dark={dark} />
              <div className="flex-1 flex flex-col gap-2 my-2">
                <ShimmerBox w="100%" h={14} rounded={6} dark={dark} />
                <ShimmerBox w="90%" h={14} rounded={6} dark={dark} />
                <ShimmerBox w="65%" h={14} rounded={6} dark={dark} />
              </div>
              <ShimmerBox w={100} h={14} rounded={6} dark={dark} className="mb-2" />
              <div className="flex items-center gap-3 pt-4 border-t border-gray-100 dark:border-gray-800">
                <ShimmerBox w={40} h={40} rounded={99} dark={dark} />
                <div className="flex flex-col gap-1.5 flex-1">
                  <ShimmerBox w={120} h={14} rounded={6} dark={dark} />
                  <ShimmerBox w={80} h={12} rounded={6} dark={dark} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  </>
);

/* ── FAQ Shimmer ────────────────────────────────────────────────────── */
export const FaqShimmer = ({ count = 5, dark = false }) => (
  <>
    <ShimmerStyles />
    <section id="faq" className="pt-6 pb-16 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 flex flex-col items-center gap-3">
          <ShimmerBox w={320} h={36} rounded={10} dark={dark} />
          <ShimmerBox w={400} h={18} rounded={6} dark={dark} className="max-w-full" />
        </div>

        <div className="flex flex-col space-y-4">
          {[...Array(count)].map((_, i) => (
            <div
              key={i}
              className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl px-6 py-5 flex items-center justify-between shadow-sm"
            >
              <ShimmerBox w={`${65 + (i % 3) * 10}%`} h={20} rounded={6} dark={dark} />
              <ShimmerBox w={32} h={32} rounded={99} dark={dark} />
            </div>
          ))}
        </div>
      </div>
    </section>
  </>
);

/* ── Section header shimmer (title + subtitle + link) ───────── */
export const SectionHeaderShimmer = ({ dark = false }) => (
  <>
    <ShimmerStyles />
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 32 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <ShimmerBox w={40} h={40} rounded={12} dark={dark} />
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <ShimmerBox w={140} h={20} rounded={6} dark={dark} />
          <ShimmerBox w={180} h={13} rounded={6} dark={dark} />
        </div>
      </div>
      <ShimmerBox w={70} h={16} rounded={6} dark={dark} />
    </div>
  </>
);

/* ── FAQ Accordion list shimmer ─────────────────────────────── */
export const FaqAccordionShimmer = ({ count = 5, dark = false }) => (
  <>
    <ShimmerStyles />
    <div className="flex flex-col gap-3">
      {[70, 85, 55, 75, 60].slice(0, count).map((w, i) => (
        <div
          key={i}
          className="rounded-xl border border-gray-100 dark:border-gray-800/60 bg-white dark:bg-gray-800/80 p-5 shadow-xs flex items-center justify-between gap-4 transition-all"
        >
          <div className="flex-1 max-w-xl">
            <ShimmerBox w={`${w}%`} h={18} rounded={6} dark={dark} />
          </div>
          <ShimmerBox w={28} h={28} rounded={99} dark={dark} className="shrink-0" />
        </div>
      ))}
    </div>
  </>
);

/* ── FAQ Page shimmer (2-column layout matching /faq) ───────── */
export const FaqPageShimmer = ({ dark = false }) => (
  <>
    <ShimmerStyles />
    <div className="flex flex-col lg:flex-row gap-6 w-full animate-pulse-subtle">
      {/* Mobile: pill strip */}
      <div className="lg:hidden flex overflow-hidden gap-2 pb-1">
        {[100, 120, 90, 110].map((w, i) => (
          <div
            key={i}
            className="px-3.5 py-2 rounded-xl bg-white dark:bg-[var(--surface-card)] border border-gray-200 dark:border-[var(--surface-border)] shrink-0 flex items-center gap-2"
          >
            <ShimmerBox w={14} h={14} rounded={99} dark={dark} />
            <ShimmerBox w={w - 40} h={14} rounded={4} dark={dark} />
          </div>
        ))}
      </div>

      {/* Desktop: sidebar */}
      <div className="hidden lg:block w-60 xl:w-64 shrink-0">
        <div className="bg-white dark:bg-[var(--surface-card)] rounded-2xl border border-gray-100 dark:border-[var(--surface-border)] shadow-xs overflow-hidden p-3 sticky top-6">
          <ShimmerBox w={110} h={12} rounded={4} dark={dark} className="mb-4 mt-1 mx-1" />
          <div className="flex flex-col gap-2">
            {[130, 150, 120, 140, 110].map((w, i) => (
              <div key={i} className="flex items-center justify-between p-2.5 rounded-xl bg-gray-50/50 dark:bg-white/5">
                <div className="flex items-center gap-3 flex-1">
                  <ShimmerBox w={20} h={20} rounded={6} dark={dark} />
                  <ShimmerBox w={w} h={14} rounded={6} dark={dark} />
                </div>
                <ShimmerBox w={20} h={18} rounded={6} dark={dark} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* RIGHT: Questions */}
      <div className="flex-1 min-w-0">
        {/* Section header banner shimmer */}
        <div className="flex items-center justify-between mb-5 bg-white dark:bg-[var(--surface-card)] p-4 rounded-xl border border-gray-100 dark:border-[var(--surface-border)] shadow-2xs">
          <div className="flex items-center gap-3">
            <ShimmerBox w={32} h={32} rounded={8} dark={dark} />
            <ShimmerBox w={160} h={18} rounded={6} dark={dark} />
          </div>
          <ShimmerBox w={80} h={14} rounded={4} dark={dark} />
        </div>

        {/* Accordion list */}
        <div className="flex flex-col gap-3">
          {[72, 88, 54, 79, 63, 81].map((w, i) => (
            <div
              key={i}
              className="rounded-xl border border-gray-100 dark:border-[var(--surface-border)] bg-white dark:bg-[var(--surface-card)] p-5 shadow-xs flex items-center justify-between gap-4"
            >
              <div className="flex-1">
                <ShimmerBox w={`${w}%`} h={18} rounded={6} dark={dark} />
                {i === 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100 dark:border-[var(--surface-border)] space-y-2">
                    <ShimmerBox w="95%" h={13} rounded={4} dark={dark} />
                    <ShimmerBox w="85%" h={13} rounded={4} dark={dark} />
                    <ShimmerBox w="60%" h={13} rounded={4} dark={dark} />
                  </div>
                )}
              </div>
              <ShimmerBox w={28} h={28} rounded={99} dark={dark} className="shrink-0" />
            </div>
          ))}
        </div>
      </div>
    </div>
  </>
);

export default ShimmerStyles;
